/**
 * The list page's rules, with no React in them.
 *
 * Kept apart so the two decisions worth being sure about - how old a run is
 * allowed to get before it stops being evidence, and which tone a row wears -
 * can be tested without mounting anything.
 */
import type { BrainListRow } from "../../../lib/siteBrain";
import type { StatusTone } from "../../ui";

/**
 * When a run stops counting as current.
 *
 * The same 30 days the letter's freshness check uses: a finding older than
 * that has usually been fixed, and quoting it at a prospect is how an audit
 * mail turns into a reason to distrust the sender.
 */
export const STALE_AFTER_DAYS = 30;

/** Whole days between two instants, floored. */
export function daysBetween(from: string | null, now: Date): number | null {
  if (!from) return null;
  const then = Date.parse(from);
  if (Number.isNaN(then)) return null;
  return Math.floor((now.getTime() - then) / 86_400_000);
}

/** A finished run whose evidence has aged out. */
export function isStale(row: BrainListRow, now: Date): boolean {
  if (row.status !== "done") return false;
  const age = daysBetween(row.finishedAt, now);
  return age !== null && age >= STALE_AFTER_DAYS;
}

/**
 * What the row's status pill looks like.
 *
 * A degraded run is deliberately NOT "ok". It finished, so the column says
 * "done", but half of it may not have happened - and a green pill over a run
 * with no pictures and no AI passes is the interface telling a lie the row
 * itself does not.
 */
export function statusTone(row: BrainListRow): StatusTone {
  if (row.status === "running") return "info";
  if (row.status === "error") return "lt";
  return row.degraded.length > 0 ? "wt" : "ok";
}

/** The i18n key suffix for the status pill's wording. */
export function statusKey(row: BrainListRow): string {
  if (row.status === "running") return "running";
  if (row.status === "error") return "error";
  return row.degraded.length > 0 ? "degraded" : "done";
}

/**
 * Which verdict tone a fit decision wears.
 *
 * "no" is muted rather than red: the brain deciding a company is not worth
 * selling to is a useful answer, not a failure, and painting it as an error
 * would push operators to re-run it looking for a greener one.
 */
export function fitTone(fit: BrainListRow["fit"]): StatusTone {
  if (fit === "go") return "ok";
  if (fit === "hold") return "wt";
  return "mut";
}

/** Counts for the filter pills, off the rows the page already has. */
export interface BrainListCounts {
  running: number;
  error: number;
  stale: number;
}

export function countStates(rows: BrainListRow[], now: Date): BrainListCounts {
  let running = 0;
  let error = 0;
  let stale = 0;
  for (const row of rows) {
    if (row.status === "running") running += 1;
    else if (row.status === "error") error += 1;
    if (isStale(row, now)) stale += 1;
  }
  return { running, error, stale };
}

/**
 * What the row says about who started the run.
 *
 * Re-exported rather than defined here: the card on one company's page asks the
 * same question, and the rule that keeps a name out of `t()` has to be the same
 * rule in both places. The import path stays this file so the list and its test
 * do not move.
 */
export { starterLabel, type StarterLabel } from "../../../lib/brainStarter";
